import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { lazy, Suspense, useEffect, useState, useRef } from "react";
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { Toaster } from "./components/ui/sonner";
import { ThemeProvider } from "./context/ThemeContext";
import { CreditProvider } from "./context/CreditContext";
import { CMSVisibilityProvider } from "./context/CMSVisibilityContext";
import { SiteContentProvider } from "./contexts/SiteContentContext";
import { LanguageProvider } from "./context/LanguageContext";
import { SiteTextsProvider } from "./context/SiteTextsContext";
import { NavbarProvider } from "./context/NavbarContext";
import { AuthModalProvider } from "./context/AuthModalContext";
import MainLayout from "./layouts/MainLayout";
import { GoogleOAuthProvider } from '@react-oauth/google';

// Critical pages loaded immediately
import Landing from "./pages/Landing";
import ProtectedRoute from "./components/ProtectedRoute";
import ProtectedAdminRoute from "./components/ProtectedAdminRoute";

// Lazy load all other pages for faster initial load
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const TopicDetail = lazy(() => import("./pages/TopicDetail"));
const TopicCheckout = lazy(() => import("./pages/TopicCheckout"));
const TestInterface = lazy(() => import("./pages/TestInterface"));
const Results = lazy(() => import("./pages/Results"));
const SharedResult = lazy(() => import("./pages/SharedResult"));
const CertificateVerify = lazy(() => import("./pages/CertificateVerify"));
const UserCertificates = lazy(() => import("./pages/UserCertificates"));
const Analytics = lazy(() => import("./pages/Analytics"));
const TestHistory = lazy(() => import("./pages/TestHistory"));
const TestReview = lazy(() => import("./pages/TestReview"));
const Leaderboard = lazy(() => import("./pages/Leaderboard"));
const Credits = lazy(() => import("./pages/Credits"));
const GuestTest = lazy(() => import("./pages/GuestTest"));
const Articles = lazy(() => import("./pages/Articles"));
const ArticleDetail = lazy(() => import("./pages/ArticleDetail"));
const NotFound = lazy(() => import("./pages/NotFound"));
const CreateQuiz = lazy(() => import("./pages/CreateQuiz"));
const ManageQuiz = lazy(() => import("./pages/ManageQuiz"));
const BrowseQuizzes = lazy(() => import("./pages/BrowseQuizzes"));
const QuizBySlug = lazy(() => import("./pages/QuizBySlug"));
const MyCreations = lazy(() => import("./pages/MyCreations"));
const LiveLobbies = lazy(() => import("./pages/LiveLobbies"));
const LiveResolver = lazy(() => import("./pages/LiveResolver"));
const GamesIndex = lazy(() => import("./pages/games/GamesIndex"));
const GameAvailabilityGuard = lazy(() => import("./components/games/GameAvailabilityGuard"));
const TugOfWar = lazy(() => import("./pages/games/TugOfWar"));
const KnowledgeClimb = lazy(() => import("./pages/games/KnowledgeClimb"));
const SpeedSprint = lazy(() => import("./pages/games/SpeedSprint"));
const RocketRace = lazy(() => import("./pages/games/RocketRace"));

// Admin pages lazy loaded
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminActivate = lazy(() => import("./pages/AdminActivate"));
const AdminLayout = lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminQuestions = lazy(() => import("./pages/admin/AdminQuestions"));
const AdminTopics = lazy(() => import("./pages/admin/AdminTopics"));
const AdminSettings = lazy(() => import("./pages/admin/AdminSettings"));
const AdminContent = lazy(() => import("./pages/admin/AdminContent"));
const AdminLanguages = lazy(() => import("./pages/admin/AdminLanguages"));
const AdminCredits = lazy(() => import("./pages/admin/AdminCredits"));
const AdminPurchases = lazy(() => import("./pages/admin/AdminPurchases"));
const AdminAnnouncements = lazy(() => import("./pages/admin/AdminAnnouncements"));
const AdminArticles = lazy(() => import("./pages/admin/AdminArticles"));
const AdminCMS = lazy(() => import("./pages/admin/AdminCMS"));
const AdminCreditRequests = lazy(() => import("./pages/admin/AdminCreditRequests"));
const AdminManagement = lazy(() => import("./pages/admin/AdminManagement"));
const AdminQuizzes = lazy(() => import("./pages/admin/AdminQuizzes"));
const AdminCategories = lazy(() => import("./pages/admin/AdminCategories"));
const AdminGameSettings = lazy(() => import("./pages/admin/AdminGameSettings"));
const AdminRewards = lazy(() => import("./pages/admin/AdminRewards"));
const AdminGameAttempts = lazy(() => import("./pages/admin/AdminGameAttempts"));
const AdminMarketing = lazy(() => import("./pages/admin/AdminMarketing"));
const Rewards = lazy(() => import("./pages/Rewards"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOOGLE_CLIENT_ID = "402441223364-b12n8btr9i5j0l90up137eevef4ufsf2.apps.googleusercontent.com";

// Minimal loading spinner for lazy-loaded chunks. Uses the app page bg so it
// blends with both the prior page and the incoming page — no dark flash.
const PageLoader = () => (
  <div
    className="min-h-screen flex items-center justify-center"
    style={{ background: '#f3f6fa' }}
  >
    <div className="w-8 h-8 border-3 border-brand-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

function AppRouter() {
  const location = useLocation();
  
  // Legacy auth callback handler
  if (location.hash?.includes('session_id=')) {
    return <Navigate to="/" replace />;
  }
  
  // NOTE: the old <PageTransition> cross-fade wrapper was removed. Forcing
  // the outgoing page to opacity 0 before swapping in the new page created a
  // visible "blink to blank, then content" flicker on every navigation.
  // With MainLayout persisting the shell and react-query caching content,
  // the native route swap is now instant and flicker-free.
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
          <Route path="/guest-test/:attemptId" element={<GuestTest />} />
          <Route path="/shared-result/:attemptId" element={<SharedResult />} />
          <Route path="/certificate/:certificateId" element={<CertificateVerify />} />
          <Route path="/verify/:certificateId" element={<CertificateVerify />} />
          <Route path="/dashboard" element={<Navigate to="/" replace />} />

          {/* Routes that share the UnifiedNavbar shell. The navbar is rendered
              once in MainLayout and stays mounted across navigations within
              this group — only the page content swaps.
              
              Landing (/) is intentionally inside MainLayout too. Without
              this, clicking the brand logo from any other page would
              navigate to /, which — living outside the layout — would
              force a full MainLayout unmount-then-remount, producing a
              flash the user reported. */}
          <Route element={<MainLayout />}>
            <Route path="/" element={<Landing />} />
            <Route path="/live-lobbies" element={<LiveLobbies />} />
            <Route path="/live/:code" element={<LiveResolver />} />
            <Route path="/articles" element={<Articles />} />
            <Route path="/articles/:articleId" element={<ArticleDetail />} />
            <Route path="/topic/:topicId" element={<TopicDetail />} />
            <Route path="/checkout/:topicId" element={<ProtectedRoute><TopicCheckout /></ProtectedRoute>} />
            <Route path="/results/:attemptId" element={<ProtectedRoute><Results /></ProtectedRoute>} />
            <Route path="/analytics" element={<ProtectedRoute><Analytics /></ProtectedRoute>} />
            <Route path="/test-history" element={<ProtectedRoute><TestHistory /></ProtectedRoute>} />
            <Route path="/test-review/:attemptId" element={<ProtectedRoute><TestReview /></ProtectedRoute>} />
            <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
            <Route path="/credits" element={<ProtectedRoute><Credits /></ProtectedRoute>} />
            {/* `/subscription` is the canonical new URL post-pivot from
                pay-per-action credits to time-based access. Keep
                `/credits` mounted to the same page so historical links,
                marketing emails, and the IremboPay return URL all keep
                working without a 404. */}
            <Route path="/subscription" element={<ProtectedRoute><Credits /></ProtectedRoute>} />
            <Route path="/credits/success" element={<ProtectedRoute><Credits /></ProtectedRoute>} />
            <Route path="/certificates" element={<ProtectedRoute><UserCertificates /></ProtectedRoute>} />
            {/* Quiz creation + management + browsing + games all share
                the same UnifiedNavbar. Their page-specific actions
                (save draft, room code, etc.) render inline in the page
                content, not inside the nav. */}
            <Route path="/create" element={<CreateQuiz />} />
            <Route path="/manage-quiz/:slug" element={<ProtectedRoute><ManageQuiz /></ProtectedRoute>} />
            <Route path="/quizzes" element={<BrowseQuizzes />} />
            <Route path="/my-creations" element={<ProtectedRoute><MyCreations /></ProtectedRoute>} />
            <Route path="/games" element={<GamesIndex />} />
            <Route path="/games/tug-of-war/:slug"      element={<GameAvailabilityGuard slug="tug-of-war"><TugOfWar /></GameAvailabilityGuard>} />
            <Route path="/games/knowledge-climb/:slug" element={<GameAvailabilityGuard slug="knowledge-climb"><KnowledgeClimb /></GameAvailabilityGuard>} />
            <Route path="/games/speed-sprint/:slug"    element={<GameAvailabilityGuard slug="speed-sprint"><SpeedSprint /></GameAvailabilityGuard>} />
            <Route path="/games/rocket-race/:slug"     element={<GameAvailabilityGuard slug="rocket-race"><RocketRace /></GameAvailabilityGuard>} />
            <Route path="/rewards" element={<Rewards />} />
            {/* Root-level short-slug share link (e.g. /fteee929). Kept last
                inside MainLayout so all named routes above win the match. */}
            <Route path="/:slug" element={<QuizBySlug />} />
          </Route>

          {/* Routes with their own custom headers — no shared layout. */}
          <Route path="/test/:attemptId" element={<ProtectedRoute><TestInterface /></ProtectedRoute>} />
          
          {/* Admin Routes */}
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/activate/:token" element={<AdminActivate />} />
          <Route path="/admin" element={<ProtectedAdminRoute><AdminLayout /></ProtectedAdminRoute>}>
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="questions" element={<AdminQuestions />} />
            <Route path="topics" element={<AdminTopics />} />
            <Route path="content" element={<AdminContent />} />
            <Route path="languages" element={<AdminLanguages />} />
            <Route path="credits" element={<AdminCredits />} />
            <Route path="purchases" element={<AdminPurchases />} />
            <Route path="credit-requests" element={<AdminCreditRequests />} />
            <Route path="announcements" element={<AdminAnnouncements />} />
            <Route path="articles" element={<AdminArticles />} />
            <Route path="cms" element={<AdminCMS />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="admins" element={<AdminManagement />} />
            <Route path="quizzes" element={<AdminQuizzes />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="games" element={<AdminGameSettings />} />
            <Route path="rewards" element={<AdminRewards />} />
            <Route path="attempts" element={<AdminGameAttempts />} />
            <Route path="marketing" element={<AdminMarketing />} />
          </Route>
          
          {/* 404 Catch-all route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <ThemeProvider>
          <LanguageProvider>
            <SiteTextsProvider>
              <SiteContentProvider>
                <CMSVisibilityProvider>
                  <CreditProvider>
                    <div className="App">
                      <BrowserRouter>
                        <NavbarProvider>
                          <AuthModalProvider>
                            <AppRouter />
                          </AuthModalProvider>
                        </NavbarProvider>
                        <Toaster position="bottom-right" />
                      </BrowserRouter>
                    </div>
                  </CreditProvider>
                </CMSVisibilityProvider>
              </SiteContentProvider>
            </SiteTextsProvider>
          </LanguageProvider>
        </ThemeProvider>
      </GoogleOAuthProvider>
    </QueryClientProvider>
  );
}

export default App;
export { API, GOOGLE_CLIENT_ID };
